import App from "@frog/source/models/App.js"
import Step from "@frog/source/models/Step.js"
import Navigation from "@frog/source/engine/navigating/Navigation.js"

import main from "@frog/source/data/app.js"
import skills from "@frog/source/data/skills.js"
import games from "@frog/source/data/games/index.js"
import * as PlotSteps from "@frog/source/data/plot/steps.js"
import * as PlotFlourishes from "@frog/source/data/plot/flourishes.js"

import Audiomix from "@frog/source/engine/Audiomix.js"

import * as Motions from "@frog/source/engine/animating/utility/Motions.js"
import Timings from "@frog/source/engine/animating/utility/Timings.js"

export function convertScriptStringToEvents(scriptString, stage) {
    const cuebox = {
        "lines": [],
        "tags": [],
    }

    if(scriptString == undefined) return
    scriptString.split("\n").forEach((singleLineString) => {
        const parts = singleLineString.trim().split("#")
        cuebox.lines.push(parts[0])
        parts.shift()
        parts.forEach((tag) => {
            cuebox.tags.push(tag.trim())
        })
    })

    return convertBoxToSteps({
        "cuebox": cuebox,
        "stage": stage
    })
}

export function convertBoxToSteps({cuebox, stage}) {
    const returnedSteps = []

    // cut cueless boxes as a single dialogue box with no further processing
    if(cuebox.lines[0].split(":").length == 1
    && cuebox.lines[0].includes(">") == false) {
        let compiledText = ""
        cuebox.lines.map((words) => {
            if(words.trim() != "") compiledText += words.trim() + "\n"
        })
        return [Step.instantiate({
            "type": "StartDialogue",
            "alphastage": stage,
            "deltastages": [
                {
                    "mark": 0,
                    "dialogue": {
                        "text": compiledText,
                    },
                }
            ]
        })]
    }

    // split the cue before the colon from the words after
    const cueWords = cuebox.lines[0].split(":")
    let keyword = cueWords[0].toLowerCase()
    const alias = cueWords[0].includes(" (") ? cueWords[0].split(" (")[0] : undefined
    // with the cue successfully extracted, reunite the colons inside the textbox
    cueWords.shift()
    cuebox.lines[0] = cueWords.join(":")

    // if there's a command immediately after the cue, extract that too
    let words = cuebox.lines[0].trim().split(" ").map((word) => word.trim())
    let actWord = undefined
    if(words[0] != undefined) {
        actWord = words[0].toLowerCase()
        if((RIBBIT_COMMANDS[keyword] != undefined
        && RIBBIT_COMMANDS[keyword][actWord] != undefined)
        || (mapKeywordToUnitKey(keyword, stage) != undefined
        && RIBBIT_COMMANDS["unitkey"][actWord] != undefined)) {
            words.shift()
        }
    }
    // if the first word is a caret, then what follows is definitely a command
    if(words[0] == ">") {
        keyword = words[0]
        actWord = words[1].toLowerCase()
        words.shift()
        words.shift()
    }

    const performerKey = mapKeywordToUnitKey(keyword, stage) || keyword
    const newDialogue = {
        "text": "",
        "data": {
            "speakerKey": performerKey,
            "speakerName": alias,
        },
        "tags": {},
        "directions": [],
        "speakingCharacter": performerKey,
    }

    const portraitScene = structuredClone(stage.dialogue?.portraitScene) || {}
    if(portraitScene[performerKey] == undefined) {
        portraitScene[performerKey] = {
            "key": performerKey,
            "isPoppingIn": true,
        }
        const sceneUnits = Object.values(portraitScene)
        if(sceneUnits.find((unit) => unit.position == "1") == undefined) {
            portraitScene[performerKey].position = "1"
            portraitScene[performerKey].direction = "right"
        } else if(sceneUnits.find((unit) => unit.position == "2") == undefined) {
            portraitScene[performerKey].position = "2"
            portraitScene[performerKey].direction = "right"
        } else if(sceneUnits.find((unit) => unit.position == "3") == undefined) {
            portraitScene[performerKey].position = "3"
            portraitScene[performerKey].direction = "left"
        } else if(sceneUnits.find((unit) => unit.position == "4") == undefined) {
            portraitScene[performerKey].position = "4"
            portraitScene[performerKey].direction = "left"
        }
    }

    // Commands
    const commandEvent = convertRibbitToEvent({performerKey, keyword, actWord, words, stage})
    if(commandEvent != undefined) {
        returnedSteps.push(commandEvent)
        cuebox.lines.shift()
    }
    // Entrances
    if(actWord == "enters") {
        if(performerKey == undefined) console.error("requesting character entrance without specifying the actor. Ignoring for now")
        portraitScene[performerKey] = {
            "key": performerKey,
            "isPoppingIn": false,
        }
        if(words[0] != undefined) {
            let position = words[0]
            if(position == "stage_left") position = "2"
            if(position == "stage_right") position = "3"
            if(position == "stage_left_behind") position = "1"
            if(position == "stage_right_behind") position = "4"
            portraitScene[performerKey]["position"] = position
        }
        cuebox.lines.shift()
    }
    // Exits
    if(actWord == "exits") {
        if(performerKey == undefined) console.error("requesting character exit without specifying the actor. Ignoring for now")
        delete portraitScene[performerKey]
        cuebox.lines.shift()
    }

    for(let words of cuebox.lines) {
        words = words.trim()
        if(words != "") {
            if(words.startsWith("- ")
            || words.startsWith("* ")) {
                const parts = words.slice(2).split("-> ")
                const flagThrowParts = parts[1].split("=")
                if(flagThrowParts.length < 2) {
                    console.error("Dialogue choice fails to throw flag", words)
                }
                if(newDialogue.choices == undefined) newDialogue.choices = []
                newDialogue.choices.push({
                    "text": parts[0],
                    "result": {
                        "flagname": flagThrowParts[0],
                        "value": flagThrowParts[1],
                    },
                })
            } else {
                newDialogue.text += words + "\n"
            }
        }
    }

    // Tag the box
    cuebox.tags.forEach((tag) => {
        if(tag.includes("=")) {
            const key = tag.split("=")[0]
            const value = tag.split("=")[1]
            newDialogue.tags[key] = value
        } else {
            if(tag.includes(":")) {
                const propertyKey = tag.split(":")[0].trim()
                const value = tag.split(":")[1].trim()
                portraitScene[performerKey][propertyKey] = value
            } else {
                newDialogue.tags["_"] = tag
            }
        }
    })

    // Close the box
    newDialogue.data.scene = {"units": Object.values(portraitScene)}
    if(portraitScene[performerKey]?.isPoppingIn == true) {
        delete portraitScene[performerKey]
    }
    newDialogue.portraitScene = portraitScene
    if(newDialogue.choices?.length > 0) {
        newDialogue.type = "DialogueChoice"
        newDialogue.data.flagname = ""
        newDialogue.data.options = newDialogue.choices
        returnedSteps.push(Step.instantiate({
            "type": "StartDialogue",
            "duration": Infinity,
            "isImportant": true,
            "isUnskippable": true,
            "isBranching": true,
            "alphastage": stage,
            "deltastages": [
                {
                    "mark": 0,
                    "dialogue": newDialogue,
                },
                // ...PlotFlourishes.BobMovement(stage.units[performerKey]),
            ]
        }))
    } else if(newDialogue.text != "") {
        returnedSteps.push(Step.instantiate({
            "type": "StartDialogue",
            "alphastage": stage,
            "deltastages": [
                {
                    "mark": 0,
                    "dialogue": newDialogue,
                },
                // ...PlotFlourishes.BobMovement(stage.units[performerKey]),
            ]
        }))
    } else {
        if(Object.keys(portraitScene).length > 0) {
            returnedSteps.push(Step.instantiate({
                "type": "UpdatePortraits",
                "alphastage": stage,
                "deltastages": [
                    {
                        "dialogue": newDialogue,
                    }
                ]
            }))
        }
    }

    return returnedSteps
}

export const RIBBIT_COMMANDS = {
    "title": function({words}) {
        const stanzas = words.join(" ").split("/")
        return PlotSteps.ChapterStart(stanzas)
    },
    "scene": {
        "cuts_to": function({words}) {
            let destination = words.join("/")
            if(Object.keys(games[main.defaultGameSlug]).includes(words[0])) {
                destination = "/games/" + main.defaultGameSlug + "/chapters/" + words[0]
            }
            if(words[0] == "main" && words[1] == "menu") {
                destination = "/games/" + main.defaultGameSlug
            }

            return {"func": () => Navigation.go(destination)}
        },
        "restarts": function({words}) {
            return {"func": () => App.game.chapter.restart()}
        },
        "advances_to": function({words, stage}) {
            return PlotSteps.MovePlayerPartyToNewEncounter({"nextEncounterKey": "" + words[0]})(stage)
        },
    },
    "banner": {
        "success": function() {
            return {"type": "chapter success"}
        },
        "failure": function() {
            return {"type": "chapter failure"}
        }
    },
    "music": function({words}) {
        if(words[0] == undefined) console.error("attempting to play sound with no name")
        return {"func": () => Audiomix.trigger(words[0]), "isIgnorable": true}
    },
    ">": {
        "executes": function({words}) {
            let options = {}
            const plotscriptName = words[0]
            words.shift()
            for(let word of words) {
                const optionKey = word.split(":")[0]
                const optionValue = word.split(":")[1]
                options[optionKey] = optionValue
            }
            return PlotSteps[plotscriptName](options)(stage)
        },
        "waits": function({words}) {
            let waitTime = Number.parseInt(words[0])
            if(isNaN(waitTime)) {
                console.error("attempting to wait a non-number amount of time", waitTime)
            }
            return PlotSteps.Wait(waitTime)
        }
    },
    "unitkey": {
        "attacks": function({performerKey, words, stage}) {
            const unitDamageSkills = stage.units[performerKey].skillKeys.filter((skillKey) => skillKey != undefined && skills[skillKey].role == "damage")
            const positions = interpretTextToPositions({words, stage})
            const steps = calculateWalkPathSteps({
                "unitKey": performerKey,
                "stage": stage,
                "targetPositions": positions.slice(0, positions.length - 1)
            })
            const lastMark = steps[steps.length - 1].mark
            skills[unitDamageSkills[0]].calculateStep({
                "stage": stage,
                "position": positions[positions.length - 1],
                "performerKey": performerKey,
            })[0].deltastages.forEach((step) => {
                step.mark += lastMark
                steps.push(step)
            })
            return {
                "name": "MoveUnit",
                "type": "invoke skill",
                "deltastages": steps,
            }
        },
        "moves": function({performerKey, words, stage}) {
            return {
                "name": "MoveUnit",
                "type": "invoke skill",
                "deltastages": calculateWalkPathSteps({
                    "unitKey": performerKey,
                    "stage": stage,
                    "targetPositions": interpretTextToPositions({words, stage})
                }),
            }
        },
        "supports": function({performerKey, words, stage}) {
            const unitSupportSkills = stage.units[performerKey].skillKeys.filter((skillKey) => skills[skillKey].role == "support")
            const positions = interpretTextToPositions({words, stage})
            return {
                "name": "MoveUnit",
                "type": "invoke skill",
                "deltastages": calculateWalkPathSteps({
                    "unitKey": performerKey,
                    "stage": stage,
                    "targetPositions": positions.slice(0, -2)
                }).concat(skills[unitSupportSkills[0]].calculateStep({
                    "stage": stage,
                    "position": positions[positions.length - 1],
                    "performerKey": performerKey,
                })),
            }
        },
        "enters": function() {
            return
        },
        "exits": function() {
            return
        },
    }
}
function interpretTextToPositions({words, stage}) {
    const targetPositions = []
    for(targetWord of words) {
        let targetPosition = undefined
        const targetCoordinates = targetWord.split("x")
        if(isNaN(Number.parseInt(targetCoordinates[0])) == false) {
            targetPosition =  {
                "x": Number.parseInt(targetCoordinates[0]),
                "y": Number.parseInt(targetCoordinates[1]),
            }
        }
        const targetKey = mapKeywordToUnitKey(targetWord, stage)
        if(targetKey != undefined) {
            targetPosition = {
                "x": stage.units[targetKey].position.x,
                "y": stage.units[targetKey].position.y,
                "z": stage.units[targetKey].position.z,
            }
        }
        const targetSpot = stage.world.spots[targetWord]
        if(targetSpot != undefined) {
            targetPosition = {
                "x": targetSpot.position.x,
                "y": targetSpot.position.y,
                "z": targetSpot.position.z,
            }
        }
        targetPositions.push(targetPosition)
    }
    return targetPositions
}
function calculateWalkPathSteps({unitKey, targetPositions, stage}) {
    const deltastages = [
        {
            "mark": 0,
            "unit": {
                "key": unitKey,
                "position": stage.units[unitKey].position,
            }
        },
        {
            "mark": 0,
            "audio": {
                "triggerKey": stage.units[unitKey].moveaudio || "Unit:Move"
            },
        },
    ]
    let mark = 0
    for(position of targetPositions) {
        position.z = position.z ?? 1
        mark += 100
        deltastages.push({
            "mark": mark,
            "timing": Timings.easeOutQuad,
            "motion": Motions.arc(0.5),
            "unit": {
                "key": unitKey,
                "position": position,
            }
        })
        deltastages.push({
            "mark": mark,
            "audio": {
                "triggerKey": stage.units[unitKey].moveaudio || "Unit:Move"
            },
        })
    }

    return deltastages
}

export function convertRibbitToEvent({performerKey, keyword, actWord, words, stage}) {
    if(keyword == undefined) return
    if(mapKeywordToUnitKey(keyword, stage) != undefined) {
        if(RIBBIT_COMMANDS["unitkey"][actWord] != undefined) {
            return RIBBIT_COMMANDS["unitkey"][actWord]({
                "performerKey": mapKeywordToUnitKey(keyword, stage),
                "words": words,
                "stage": stage,
            })
        } else {
            return
        }
    } else {
        const cueCommand = RIBBIT_COMMANDS[keyword]
        if(cueCommand == undefined) return
        if(cueCommand instanceof Function) return cueCommand({words, stage})
        if(cueCommand[actWord] instanceof Function) return cueCommand[actWord]({words, stage})
        return
    }
}

function mapKeywordToPartyKey(keyword) {
    for(const game of Object.values(games)) {
        const party = Object.values(game.player.party.units)

        const keyMatchedUnit = party.find((unit) => unit.key?.toLowerCase() == keyword)
        if(keyMatchedUnit != undefined) return keyMatchedUnit.key

        const nameMatchedUnit = party.find((unit) => unit.name?.toLowerCase() == keyword)
        if(nameMatchedUnit != undefined) return nameMatchedUnit.key

        const subtitleMatchedUnit = party.find((unit) => unit.subtitle?.toLowerCase() == keyword)
        if(subtitleMatchedUnit != undefined) return subtitleMatchedUnit.key

        const jobnameMatchedUnit = party.find((unit) => unit.jobName?.toLowerCase() == keyword)
        if(jobnameMatchedUnit != undefined) return jobnameMatchedUnit.key

        const aliasMatchedUnit = party.find((unit) => {
            const matchedAlias = unit.aliases?.find((alias) => {
                return alias.toLowerCase() == keyword
            })
            return matchedAlias != undefined
        })
        if(aliasMatchedUnit != undefined) return aliasMatchedUnit.key
    }

    return undefined
}
export function mapKeywordToUnitKey(keyword, stage) {

    if(keyword.includes(" (")) {
        const parentheticalKey = keyword.split(" (")[1]
        keyword = parentheticalKey.slice(0, parentheticalKey.length - 1)
    }
    keyword = keyword.toLowerCase().trim()

    const partyMatchedUnitKey = mapKeywordToPartyKey(keyword)
    if(partyMatchedUnitKey != undefined) return partyMatchedUnitKey

    const units = Object.values(stage.units)

    const keyMatchedUnit = units.find((unit) => unit.key?.toLowerCase() == keyword)
    if(keyMatchedUnit != undefined) return keyMatchedUnit.key

    const nameMatchedUnit = units.find((unit) => unit.name?.toLowerCase() == keyword)
    if(nameMatchedUnit != undefined) return nameMatchedUnit.key

    const subtitleMatchedUnit = units.find((unit) => unit.subtitle?.toLowerCase() == keyword)
    if(subtitleMatchedUnit != undefined) return subtitleMatchedUnit.key

    const jobnameMatchedUnit = units.find((unit) => unit.jobName?.toLowerCase() == keyword)
    if(jobnameMatchedUnit != undefined) return jobnameMatchedUnit.key
}
